import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import {
  fetchPractices,
  PracticeCategory,
} from '../../../../services/practices'

import { Checkbox, Grid } from '@mui/material'

interface UserPossibilitiesFormProps {
  formData: any
  handleFieldChange: (field: string, value: any) => void
}

const UserPossibilitiesForm = ({}: UserPossibilitiesFormProps) => {
  const { t } = useTranslation()
  const [expandedPanel, setExpandedPanel] = useState<number | null>(null)
  const [practices, setPractices] = useState<{ [key: string]: boolean }>({})
  const [practiceList, setPracticeList] = useState<PracticeCategory[]>([])

  useEffect(() => {
    const fetchPracticesData = async () => {
      try {
        const data = await fetchPractices()

        const currentPath = window.location.pathname
        let storageKey = 'myAnnounce'

        if (currentPath === '/dashboard/announce-work/') {
          storageKey = 'myJobOffer'
        } else if (currentPath === '/dashboard/announce-event/') {
          storageKey = 'myEvent'
        } else if (currentPath === '/dashboard/announce-establishment/') {
          storageKey = 'myEstablishment'
        }

        const myAnnounce = JSON.parse(localStorage.getItem(storageKey) || '{}')
        const myProfile = JSON.parse(localStorage.getItem('myProfile') || '{}')
        const storedPractices = myAnnounce.practices || []
        const initialPractices: { [key: string]: boolean } = {}

        const status = myAnnounce.status || []
        const massageOnly = myProfile.massageOnly || false

        let filteredData = data

        if (massageOnly) {
          filteredData = data.filter(category =>
            ['massage', 'disability'].includes(category.categoryName),
          )
        } else if (!status.includes('massage')) {
          filteredData = data.filter(
            category => category.categoryName !== 'massage',
          )
        }

        setPracticeList(filteredData)

        filteredData.forEach(category => {
          category.practices.forEach(practice => {
            initialPractices[practice.practiceName] = storedPractices.includes(
              practice.id,
            )
          })
        })

        setPractices(initialPractices)

        if (filteredData.length > 0) {
          setExpandedPanel(filteredData[0].id)
        }
      } catch (err) {
        Sentry.captureException(err, {
          extra: {
            action: 'fetchPracticesData',
            timestamp: new Date().toISOString(),
          },
          tags: {
            type: 'api_error',
            method: 'fetchPracticesData',
          },
        })
      }
    }

    fetchPracticesData()
  }, [])

  const handleCheckboxChange =
    (practiceName: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target
      setPractices(prevPractices => {
        const updatedPractices = { ...prevPractices, [practiceName]: checked }

        const currentPath = window.location.pathname
        let storageKey = 'myAnnounce'

        if (currentPath === '/dashboard/announce-work/') {
          storageKey = 'myJobOffer'
        } else if (currentPath === '/dashboard/announce-event/') {
          storageKey = 'myEvent'
        } else if (currentPath === '/dashboard/announce-establishment/') {
          storageKey = 'myEstablishment'
        }

        const myAnnounce = JSON.parse(localStorage.getItem(storageKey) || '{}')
        let practices = myAnnounce.practices || []

        const practiceId = practiceList
          .flatMap(category => category.practices)
          .find(practice => practice.practiceName === practiceName)?.id

        if (checked && practiceId) {
          if (!practices.includes(practiceId)) {
            practices.push(practiceId)
          }
        } else if (practiceId) {
          practices = practices.filter((id: number) => id !== practiceId)
        }

        myAnnounce.practices = practices
        localStorage.setItem(storageKey, JSON.stringify(myAnnounce))

        return updatedPractices
      })
    }

  return (
    <div>
      {practiceList.map(category => (
        <div
          key={category.id}
          className='accordion border-3 border-[#2c2c2c] mb-2'
        >
          <div
            className={`accordion-header flex justify-between border-b-2 border-anthraciteLight items-center 
            ${expandedPanel === category.id ? 'rounded-t-lg' : 'rounded-lg'} 
            cursor-pointer px-4 py-2 bg-anthracite text-white 
            ${expandedPanel === category.id ? 'expanded' : ''}`}
            onClick={() =>
              setExpandedPanel(
                expandedPanel === category.id ? null : category.id,
              )
            }
          >
            {t(`create-ad.${category.categoryName}`)}
            <span
              className={`text-redpink expand-icon transition-transform duration-300 ease-in-out transform ${
                expandedPanel === category.id ? 'rotate-180' : ''
              }`}
            >
              ▼
            </span>
          </div>
          {expandedPanel === category.id && (
            <div className='accordion-content border-t border-b-2 rounded-b-lg border-[#222121] p-2 border-l-2 border-r-2'>
              <Grid container spacing={1}>
                {category.practices.map((practice, index) => (
                  <Grid item xs={12} sm={6} md={4} key={practice.id}>
                    <label className='cursor-pointer block hover:bg-grisclair transition-all rounded-lg'>
                      <Checkbox
                        checked={!!practices[practice.practiceName]}
                        onChange={handleCheckboxChange(practice.practiceName)}
                        name={practice.practiceName}
                      />
                      {t(`practices.${practice.practiceName}`)}
                    </label>
                  </Grid>
                ))}
              </Grid>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export default UserPossibilitiesForm
